import { EditorState } from "lexical";
import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { AutoLinkPlugin } from "@lexical/react/LexicalAutoLinkPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";

import { ImageNode } from "components/ContentEditor/ImageNode";
import { Toolbar } from "components/ContentEditor/Toolbar";
import { loadContentIntoEditor } from "components/ContentEditor/converter/contentFormatConverter";

interface ContentEditorProps {
	defaultValue?: string;
	onChange?: (value: string) => void;
}

const URL_REGEX = /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)/;

const MATCHERS = [
	(text: string) => {
		const match = URL_REGEX.exec(text);
		return (
			match && {
				index: match.index,
				length: match[0].length,
				text: match[0],
				url: match[0].startsWith("http") ? match[0] : `https://${match[0]}`,
			}
		);
	},
];

export function ContentEditor(props: ContentEditorProps) {
	const initialConfig = {
		namespace: "ContentEditor",
		nodes: [HeadingNode, QuoteNode, TableNode, TableRowNode, TableCellNode, ListNode, ListItemNode, LinkNode, AutoLinkNode, ImageNode],
		theme: {
			root: "editor-root",
			contentEditable: "editor-content",
			heading: {
				h1: "editor-heading-h1",
				h2: "editor-heading-h2",
				h3: "editor-heading-h3",
			},
			text: {
				bold: "editor-text-bold",
				italic: "editor-text-italic",
				underline: "editor-text-underline",
				strikethrough: "editor-text-strikethrough",
			},
			link: "editor-link",
		},
		onError: (error: Error) => console.error(error),
	};

	const handleChange = useCallback(
		(editorState: EditorState) => {
			editorState.read(() => {
				const lexicalJson = JSON.stringify(editorState.toJSON());
				props.onChange?.(lexicalJson);
			});
		},
		[props]
	);

	const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
		if (e.key === "Escape") {
			e.preventDefault();
		}
	}, []);

	return (
		<ContentEditorWrapper onKeyDown={handleKeyDown} onClick={(e) => e.stopPropagation()}>
			<LexicalComposer initialConfig={initialConfig}>
				<EditorStateLoader defaultValue={props.defaultValue} />
				<Toolbar />
				<RichTextPlugin
					contentEditable={<ContentEditable className="content-editor" />}
					placeholder={<div className="editor-placeholder">Enter some text...</div>}
					ErrorBoundary={LexicalErrorBoundary}
				/>
				<LinkPlugin />
				<AutoLinkPlugin matchers={MATCHERS} />
				<ListPlugin />
				<HistoryPlugin />
				<OnChangePlugin onChange={handleChange} />
			</LexicalComposer>
		</ContentEditorWrapper>
	);
}

// Denne må være en egen komponent da den trenger contexten
function EditorStateLoader({ defaultValue }: { defaultValue?: string }) {
	const [editor] = useLexicalComposerContext();
	const [loaded, setLoaded] = useState(false);

	useEffect(() => {
		setLoaded(false);
	}, [defaultValue]);

	useEffect(() => {
		if (defaultValue && !loaded) {
			try {
				const loader = loadContentIntoEditor(defaultValue);

				if (loader) {
					editor.update(loader);
				}

				setLoaded(true);
			} catch (e) {
				console.error("Error loading editor state:", e);
			}
		}
	}, [editor, defaultValue, loaded]);

	return null;
}

const ContentEditorWrapper = styled.div`
	.editor-root {
		border: 1px solid ${(props) => props.theme.colors.formWrapperBorder};
		border-radius: 4px;
	}
	.content-editor {
		min-height: 300px;
		padding: 16px;
		outline: none;
		position: relative;
	}
	.editor-placeholder {
		color: #999;
		pointer-events: none;
		margin: 0;
		padding: 16px;
	}
	.editor-text-bold {
		font-weight: bold;
	}
	.editor-text-italic {
		font-style: italic;
	}
	.editor-text-underline {
		text-decoration: underline;
	}
	.editor-text-strikethrough {
		text-decoration: line-through;
	}
	.editor-link {
		color: #0066cc;
		text-decoration: underline;
		cursor: pointer;
		background-color: rgba(0, 102, 204, 0.05);

		&:hover {
			text-decoration: underline;
			background-color: rgba(0, 102, 204, 0.1);
		}
	}
	h1,
	h2,
	.editor-heading-h1,
	.editor-heading-h2 {
		margin: 0.5em 0;
		font-weight: bold;
	}
	.editor-heading-h1 {
		font-size: 2rem;
	}
	.editor-heading-h2 {
		font-size: 1.5rem;
	}

	ul,
	ol {
		margin: 0.5em 0;
		padding-left: 2em;
	}

	li {
		margin: 0.25em 0;
	}
`;
